import { browserPopupRedirectResolver, GoogleAuthProvider, OAuthProvider, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useContext, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import api from "../api";
import { ReactComponent as AppleLogo } from '../assets/images/apple.svg';
import { ReactComponent as GoogleLogo } from '../assets/images/google.svg';
import { ReactComponent as EmailIcon } from '../assets/images/icons/ic-email.svg';
import { ReactComponent as ArrowIcon } from '../assets/images/icons/ic-arrow.svg';
import LogoBlack from "../assets/images/logo.svg";
import MainContext from "../common/MainContext";
import { ButtonStatus } from "../common/constants";
import Button from "../components/Button";
import TextInput from "../components/TextInput";
import FooterLayout from "../components/layouts/FooterLayout";
import { auth } from "../firebase";
import typo from "../typography.module.css";
import style from "./Login.module.css";


const Login = () => {
    const navigate = useNavigate();
    const { t } = useTranslation()
    const context = useContext(MainContext)

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null)
    const [resetPassword, setResetPassword] = useState(false)
    const [emailSent, setEmailSent] = useState(false)
    const [timer, setTimer] = useState(60)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)


    const googleProvider = new GoogleAuthProvider()
    googleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    const appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email');
    appleProvider.addScope('name');
    appleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    const [nextRedirect, setNextRedirect] = useState('/')

    useEffect(() => {
        window.scrollTo({
            top: 0,
        })

        const storedRedirect = localStorage.getItem('next_redirect')
        if (storedRedirect) {
            setNextRedirect(storedRedirect)
        }

    }, [])

    useEffect(() => {
        window.addEventListener("keydown", handleEnter)
        return () => {
            window.removeEventListener("keydown", handleEnter)
        }
    }, [email, password])


    useEffect(() => {
        if (auth.currentUser) {
            navigate('/')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.currentUser])

    useEffect(() => {
        async function unlockSend() {
            for (let i = 60; i > 0; i--) {
                setTimer(i)
                await new Promise((r) => {
                    setTimeout(r, 1000)
                })
            }
            setEmailSent(false)
        }

        if (emailSent) {
            unlockSend()
        }
    }, [emailSent])

    const handleEnter = (e) => {
        if (e.code === "Enter") {
            if (resetPassword) {
                handleResetPassword()
            }
            else {
                signIn()
            }
        }
    }

    const googleSignIn = async () => {
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver)
            let user = await api.get("/teacher/")
            if (user) {
                context.setUser(user)
                navigate(nextRedirect)
                localStorage.removeItem('next_redirect')
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun docente con queste credenziali" : error.detail })
            console.error("Error Google Sign In: ", error)
        }
        setLoading(false)
        context.isLogging = false
    }

    const appleSignIn = async () => {
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithPopup(auth, appleProvider, browserPopupRedirectResolver)
            let user = await api.get("/teacher/")
            if (user) {
                context.setUser(user)
                navigate(nextRedirect)
                localStorage.removeItem('next_redirect')
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun docente con queste credenziali" : error.detail })
            console.error("Error Apple Sign In: ", error)
        }
        setLoading(false)
        context.isLogging = false
    }

    const signIn = async () => {
        context.isLogging = true
        setLoading(true)
        try {
            await signInWithEmailAndPassword(auth, email, password)
            let user = await api.get("/teacher/")
            if (user) {
                context.setUser(user)
                if (user.force_change_password) {
                    navigate("/changePassword")
                } else {
                    navigate("/")
                }
            }
        }
        catch (error) {
            await auth.signOut()
            setError({ code: -1, message: error.code === 3 ? "Nessun docente con queste credenziali" : error.detail })

            if (error.message.includes('invalid-credential')) {
                setError({ code: -1, message: t('errors.invalidCredentials') })
            }
            else {
                setError({ code: -1, message: error.message })
            }
        }
        setLoading(false)
        context.isLogging = false
    }

    const onEmailChange = (value) => {
        setEmail(value)
        if (!validateEmail(value)) {
            setError({ code: -1, message: t('errors.invalidEmail') })
        }
        else {
            setError(null)
        }
    }

    const onPasswordChange = (value) => {
        setPassword(value)
    }

    const validateEmail = (email) => {
        if (email.length > 0) {
            return email.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        }
        else {
            return true
        }
    }

    const handleChangeResetPassword = () => {
        setResetPassword(!resetPassword)
    }

    const handleResetPassword = async () => {
        setSending(true)
        try {
            // await sendPasswordResetEmail(auth, email)
            await api.get("/teacher/reset-password?email=" + email)
            setEmailSent(true)
        }
        catch (error) {
            setError({ code: -1, message: error.message })
        }
        setSending(false)
    }

    const [selectedMethod, setSelectedMethod] = useState(null)

    return (
        <FooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Login</title>
                </Helmet>
            </HelmetProvider>
            <div className={style.container}>
                {!auth.currentUser &&
                    <>
                        <div className={style.columnLeft}>

                            <div className={style.innerLeft}>

                                <div className={style.spacer}></div>
                                <div className={style.logoContainer}>
                                    <img src={LogoBlack} alt="velv logo" onClick={() => { navigate("/") }} className={style.logo} />
                                </div>
                                {!resetPassword &&
                                    <>
                                        <div className={[typo.title, style.title].join(' ')}>{t('login.title')}</div>
                                        <div className={[typo.body, style.body].join(' ')} style={{ maxWidth: '350px', marginTop: '-0.25rem' }}>{t('login.subtitle')}</div>

                                        {!selectedMethod &&
                                            <>
                                                <button className={style.googleSignIn} onClick={googleSignIn} style={{ marginTop: '1.75rem' }}>
                                                    <GoogleLogo className={style.googleLogo}></GoogleLogo>
                                                    {t('login.signInWithGoogle')}
                                                </button>
                                                <button className={style.appleSignIn} onClick={appleSignIn}>
                                                    <AppleLogo className={style.appleLogo}></AppleLogo>
                                                    {t('login.signInWithApple')}
                                                </button>
                                            </>
                                        }
                                        {selectedMethod === "email" &&
                                            <>
                                        <div className={style.formContainer}>
                                                    <Button appearance="text" onClick={() => { setSelectedMethod(null) }} style={{ display: 'flex', alignItems: 'center' }}><ArrowIcon style={{ maxHeight: '10px' }} /> Torna Indietro</Button>
                                            <TextInput type="text" placeholder={t('login.email')} onKeyUp={onEmailChange}></TextInput>
                                                    <TextInput type="password" placeholder={t('login.password')} onKeyUp={onPasswordChange}></TextInput>
                                            <div className={style.resetPassword}>

                                                <div className={style.error}>
                                                    {error &&
                                                        <span className={typo.caption}>
                                                            {error.message}
                                                        </span>
                                                    }
                                                </div>

                                                <Button
                                                    fullWidth
                                                    appearance="text"
                                                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                                                    onClick={handleChangeResetPassword}>
                                                    {t('login.forgotPassword')}
                                                </Button>
                                            </div>
                                        </div>
                                            </>
                                        }

                                        {selectedMethod === "email" &&
                                            <>
                                                <div className={typo.caption} style={{ maxWidth: '350px' }}>
                                                    Accedendo dichiari di essere maggiorenne e di aver letto e compreso le <a rel="noreferrer" style={{ textDecoration: 'underlined', color: 'var(--primary)' }} href="/terms-and-conditions" target="_blank">Condizioni d'uso</a> e la <a style={{ textDecoration: 'underlined', color: 'var(--primary)' }} href="/privacy-policy" target="_blank">Privacy Policy</a>
                                                </div>
                                                <Button
                                                    onClick={signIn}
                                                    style={{ padding: '1rem 1.5rem' }}
                                                    disabled={loading || !email || !password}
                                                    fullWidth>
                                                    LOGIN
                                                </Button>
                                            </>
                                        }

                                        {!selectedMethod &&
                                            <>
                                        <div className={style.textDivider}>
                                            <div className={style.divider}></div>
                                            <div className={style.caption}>{t('login.or')}</div>
                                            <div className={style.divider}></div>
                                        </div>
                                                <button className={style.emailSignIn} onClick={() => { setSelectedMethod("email") }}>
                                                    <EmailIcon className={style.emailLogo}></EmailIcon>
                                                    {t('login.signInWithEmail')}
                                            </button>
                                                <div className={typo.caption} style={{ maxWidth: '300px' }}>
                                                    Accedendo dichiari di essere maggiorenne e di aver letto e compreso le <a rel="noreferrer" style={{ textDecoration: 'underlined', color: 'var(--primary)' }} href="/terms-and-conditions" target="_blank">Condizioni d'uso</a> e la <a style={{ textDecoration: 'underlined', color: 'var(--primary)' }} href="/privacy-policy" target="_blank">Privacy Policy</a>
                                        </div>
                                            </>
                                        }

                                        <div className={style.spacer}></div>
                                        {/* <div className={style.registerContainer}>
                                            {t('login.noAccount')}
                                            <Button appearance="text" onClick={goToSignup}>{t('login.register')}</Button>
                                        </div> */}
                                    </>
                                }
                                {resetPassword &&
                                    <>
                                        <div className={[typo.title, style.title].join(' ')}>{t('login.resetPasswordTitle')}</div>
                                        {!emailSent &&
                                            <div className={[typo.body, style.body].join(' ')}>{t('login.resetPasswordSubtitle')}</div>
                                        }
                                        {emailSent &&
                                            <div className={[typo.body, style.body].join(' ')}>{t('login.waitToResend', { timer: timer })}</div>
                                        }
                                        <div className={style.formContainer}>
                                            <TextInput value={email} type="text" placeholder={t('login.email')} onKeyUp={onEmailChange}></TextInput>
                                            <div className={style.resetPassword}>
                                                <div className={style.error}>
                                                    {error &&
                                                        <span className={typo.caption}>
                                                            {error.message}
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <Button onClick={handleResetPassword} style={{ padding: '1rem 1.5rem' }} disabled={emailSent || sending} status={sending ? ButtonStatus.Loading : null} fullWidth>{t(emailSent ? 'login.sent' : 'login.send').toUpperCase()}</Button>
                                        <Button appearance="text" onClick={handleChangeResetPassword}>{t('login.goBack')}</Button>
                                        <div className={style.spacer}></div>
                                    </>
                                }
                            </div>
                        </div>
                        {/* <div className={style.columnRight}>
                            <div className={style.innerRight}>
                                <Splide
                                    options={{
                                        arrows: false,
                                        autoplay: true,
                                        rewind: true,
                                        speed: 500
                                    }}
                                >
                                    <SplideSlide className={style.slide}>
                                <div className={[style.rightTitle, typo.title].join(' ')}>{t('login.contentTitle')}</div>
                                <div className={[typo.body, style.rightBody].join(' ')}>{t('login.contentSubtitle')}</div>
                                        <img src={LoginIllustration} className={style.screenshot} alt="login-illustration" />
                                    </SplideSlide>
                                </Splide>
                            </div>
                        </div> */}
                    </>
                }
            </div>
        </FooterLayout>
    )

}

export default Login
